import { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';

const REVISION = 'northinvent-migration-2026-10-02';
const cardStyle = {
  padding: 20,
  border: '1px solid var(--ni-border-soft)',
  borderRadius: 12,
  background: 'var(--ni-panel)',
};

export default function DeploymentCheck() {
  const { isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const [health, setHealth] = useState(null);
  const [ready, setReady] = useState(null);
  const [authenticated, setAuthenticated] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    let active = true;
    const previousTitle = document.title;
    document.title = 'North Invent — deployment check';
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);

    async function check(path, update) {
      try {
        const response = await fetch(path, { cache: 'no-store', signal: controller.signal });
        const data = await response.json();
        if (active) update(response.ok && data.ok ? data : false);
      } catch {
        if (active) update(false);
      }
    }
    Promise.all([
      check('/api/healthz', setHealth),
      check('/api/readyz', setReady),
    ]).finally(() => clearTimeout(timeout));

    return () => {
      active = false;
      controller.abort();
      clearTimeout(timeout);
      document.title = previousTitle;
      robots.remove();
    };
  }, []);

  useEffect(() => {
    setAuthenticated(null);
    if (!isLoaded || !isSignedIn) return undefined;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    let active = true;
    async function checkSession() {
      try {
        const token = await getToken();
        if (!active) return;
        const response = await fetch('/api/org/me', {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
          signal: controller.signal,
        });
        const data = await response.json();
        if (active) setAuthenticated(response.ok && Object.prototype.hasOwnProperty.call(data, 'membership'));
      } catch {
        if (active) setAuthenticated(false);
      } finally {
        clearTimeout(timeout);
      }
    }
    checkSession();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [getToken, isLoaded, isSignedIn]);

  const backendMatches = health && health.deployment?.revision === REVISION;
  return (
    <main style={{ maxWidth: 760, margin: '40px auto', padding: '0 24px', color: 'var(--ni-text)' }}>
      <Link to="/" style={{ color: 'var(--ni-green)' }}>Back to portal</Link>
      <h1 style={{ color: 'var(--ni-text)', fontSize: 28 }}>North Invent — deployment check</h1>
      <p style={{ color: 'var(--ni-text-muted)' }}>Read-only checks of the deployed service portal.</p>
      <div style={{ display: 'grid', gap: 12 }} aria-live="polite">
        <section style={cardStyle}>
          <h2 style={{ fontSize: 18, marginTop: 0 }}>Frontend</h2>
          <p>Loaded — revision {REVISION}</p>
        </section>
        <section style={cardStyle}>
          <h2 style={{ fontSize: 18, marginTop: 0 }}>Backend through Vercel</h2>
          <p>{health === null ? 'Checking…' : backendMatches ? 'Passed — matching backend revision' : health ? 'Backend responds, but the revision does not match yet' : 'Check failed — backend could not be verified'}</p>
          {backendMatches && health.deployment.commit && <p>Commit: <code>{health.deployment.commit}</code></p>}
        </section>
        <section style={cardStyle}>
          <h2 style={{ fontSize: 18, marginTop: 0 }}>Database</h2>
          <p>{ready === null ? 'Checking…' : ready ? 'Passed — MongoDB ping through Vercel' : 'Check failed — database could not be verified'}</p>
        </section>
        <section style={cardStyle}>
          <h2 style={{ fontSize: 18, marginTop: 0 }}>Clerk</h2>
          <p>{!isLoaded ? 'Loading…' : isSignedIn ? 'Loaded — existing signed-in session' : 'Loaded — signed out'}</p>
          <p>{!isSignedIn ? 'Backend session check: sign in to test' : authenticated === null ? 'Checking the existing session with the backend…' : authenticated ? 'Passed — existing session accepted by backend' : 'Check failed — backend session could not be verified'}</p>
          <p style={{ color: 'var(--ni-text-muted)' }}>Role permissions and sign-in on the new service domain require a separate check.</p>
        </section>
      </div>
    </main>
  );
}
